import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ConfirmDialog from "./ConfirmDialog";
import NotifyCustomersLink from "./NotifyCustomersLink";
import { apiErrorMessage, apiFieldErrors } from "../services/api";
import {
  createProviderLeave,
  deleteProviderLeave,
  fetchProviderLeave,
  updateProviderLeave,
} from "../services/providerService";
import { can } from "../services/permissions";
import { getCurrentUser } from "../services/authService";
import { labelFor } from "../services/enumLabels";

const LEAVE_TYPES = ["vacation", "sick", "training", "other"];

const blank = {
  type: "vacation",
  startsOn: "",
  endsOn: "",
  isAllDay: true,
  startsAt: "09:00",
  endsAt: "13:00",
  note: "",
};

/**
 * BIZ-10 — a provider's time off.
 *
 * Leave beats the weekly schedule. The API refuses a range that would strand a
 * booking with a 422 on `starts_on`; from file 07 that check has teeth, and the
 * message it returns is rendered here as-is.
 *
 * The note is restricted. `note_restricted` on a row means one exists but this
 * caller may not read it — rendered as a lock rather than an empty field, so a
 * blank note and a hidden one are never confused.
 */
export default function ProviderLeave({ providerId }) {
  const { t } = useTranslation();

  const [leave, setLeave] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [upcomingOnly, setUpcomingOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const user = getCurrentUser();
  const mayReadNote = can(user, "staff.manage_commission");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { leave: rows, meta: pagination } = await fetchProviderLeave(providerId, {
        page,
        filters: { upcoming: upcomingOnly },
      });
      setLeave(rows);
      setMeta(pagination);
    } catch (err) {
      setError(apiErrorMessage(err, t("providers.leave.loadFailed")));
      setLeave([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [providerId, page, upcomingOnly, t]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setForm(blank);
    setEditingId(null);
    setFormError("");
    setFieldErrors({});
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      type: row.type,
      startsOn: row.starts_on,
      endsOn: row.ends_on,
      isAllDay: Boolean(row.is_all_day),
      startsAt: row.starts_at ?? "09:00",
      endsAt: row.ends_at ?? "13:00",
      // A hidden note must not be echoed back as empty and overwrite the real
      // one, so an unprivileged edit leaves the field out entirely.
      note: mayReadNote ? row.note ?? "" : null,
    });
    setEditingId(row.id);
    setFormError("");
    setFieldErrors({});
    setFormOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError("");
    setFieldErrors({});

    try {
      if (editingId) {
        await updateProviderLeave(providerId, editingId, form);
      } else {
        await createProviderLeave(providerId, form);
      }
      setFormOpen(false);
      load();
    } catch (err) {
      setFieldErrors(apiFieldErrors(err));
      setFormError(apiErrorMessage(err, t("providers.leave.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await deleteProviderLeave(providerId, deleting.id);
      setDeleting(null);
      load();
    } catch (err) {
      setDeleteError(apiErrorMessage(err, t("providers.leave.deleteFailed")));
    } finally {
      setDeleteBusy(false);
    }
  };

  const update = (changes) => setForm((current) => ({ ...current, ...changes }));

  return (
    <div className="card p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold">{t("providers.leave.title")}</h2>
          <p className="text-sm text-ink-2">{t("providers.leave.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-2">
            <input
              type="checkbox"
              checked={upcomingOnly}
              onChange={(e) => {
                setPage(1);
                setUpcomingOnly(e.target.checked);
              }}
            />
            {t("providers.leave.upcomingOnly")}
          </label>
          <button type="button" className="btn-primary" onClick={openNew}>
            {t("providers.leave.new")}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      {formOpen && (
        <form onSubmit={submit} className="mb-4 rounded-lg border border-line p-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="field-label" htmlFor="leave-type">
                {t("providers.leave.type")}
              </label>
              <select
                id="leave-type"
                className="field-input"
                value={form.type}
                onChange={(e) => update({ type: e.target.value })}
              >
                {LEAVE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {labelFor("leave_type", type)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="field-label" htmlFor="leave-starts-on">
                {t("providers.leave.from")}
              </label>
              <input
                id="leave-starts-on"
                type="date"
                className="field-input"
                value={form.startsOn}
                onChange={(e) => update({ startsOn: e.target.value })}
              />
              {fieldErrors.starts_on && <p className="field-error">{fieldErrors.starts_on[0]}</p>}
              {/* Bookings stand in the range: tell those customers first; they
                  can reschedule or cancel free, then the leave can be saved. */}
              {fieldErrors.starts_on && form.startsOn && (
                <NotifyCustomersLink
                  className="mt-1"
                  type="staff_urgent_leave"
                  scope={{ staff_id: providerId, date_from: form.startsOn, date_to: form.endsOn || form.startsOn }}
                />
              )}
            </div>

            <div>
              <label className="field-label" htmlFor="leave-ends-on">
                {t("providers.leave.to")}
              </label>
              <input
                id="leave-ends-on"
                type="date"
                className="field-input"
                value={form.endsOn}
                onChange={(e) => update({ endsOn: e.target.value })}
              />
              {fieldErrors.ends_on && <p className="field-error">{fieldErrors.ends_on[0]}</p>}
            </div>
          </div>

          <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isAllDay}
              onChange={(e) => update({ isAllDay: e.target.checked })}
            />
            {t("providers.leave.allDay")}
          </label>

          {!form.isAllDay && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="field-label" htmlFor="leave-starts-at">
                  {t("providers.leave.startTime")}
                </label>
                <input
                  id="leave-starts-at"
                  type="time"
                  className="field-input"
                  value={form.startsAt}
                  onChange={(e) => update({ startsAt: e.target.value })}
                />
                {fieldErrors.starts_at && <p className="field-error">{fieldErrors.starts_at[0]}</p>}
              </div>
              <div>
                <label className="field-label" htmlFor="leave-ends-at">
                  {t("providers.leave.endTime")}
                </label>
                <input
                  id="leave-ends-at"
                  type="time"
                  className="field-input"
                  value={form.endsAt}
                  onChange={(e) => update({ endsAt: e.target.value })}
                />
                {fieldErrors.ends_at && <p className="field-error">{fieldErrors.ends_at[0]}</p>}
              </div>
            </div>
          )}

          {/* Only offered to a caller who may read it back. */}
          {mayReadNote && (
            <div className="mt-3">
              <label className="field-label" htmlFor="leave-note">
                {t("providers.leave.note")}
              </label>
              <textarea
                id="leave-note"
                rows={2}
                className="field-input"
                value={form.note ?? ""}
                onChange={(e) => update({ note: e.target.value })}
              />
              <p className="mt-1 text-xs text-ink-2">{t("providers.leave.noteHint")}</p>
            </div>
          )}

          {formError && <p className="field-error mt-2">{formError}</p>}

          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setFormOpen(false)}
              disabled={saving}
            >
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="py-8 text-center text-sm text-ink-2">{t("common.loading")}</p>
      ) : leave.length === 0 ? (
        <p className="py-10 text-center text-sm text-ink-2">{t("providers.leave.empty")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-start text-sm">
            <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
              <tr>
                <th className="px-3 py-2">{t("providers.leave.type")}</th>
                <th className="px-3 py-2">{t("providers.leave.dates")}</th>
                <th className="px-3 py-2">{t("providers.leave.hours")}</th>
                <th className="px-3 py-2">{t("providers.leave.note")}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {leave.map((row) => (
                <tr key={row.id} className="border-b border-line last:border-0">
                  <td className="px-3 py-2">{labelFor("leave_type", row.type)}</td>
                  <td className="px-3 py-2">
                    <span dir="ltr">
                      {row.starts_on} → {row.ends_on}
                    </span>
                    <span className="ms-2 text-xs text-ink-2">
                      {t("providers.leave.dayCount", { count: row.days })}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {row.is_all_day ? (
                      <span className="text-xs text-ink-2">{t("providers.leave.allDay")}</span>
                    ) : (
                      <span dir="ltr" className="text-xs">
                        {row.starts_at} – {row.ends_at}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {row.note ? (
                      <span className="text-xs text-ink-2">{row.note}</span>
                    ) : row.note_restricted ? (
                      <span className="text-xs text-ink-2">
                        {t("providers.leave.noteHidden")}
                      </span>
                    ) : (
                      <span className="text-xs text-ink-2">{t("common.none")}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-end">
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        className="text-sm font-medium text-accent-text hover:underline"
                        onClick={() => openEdit(row)}
                      >
                        {t("common.edit")}
                      </button>
                      <button
                        type="button"
                        className="text-sm font-medium text-danger-text hover:underline"
                        onClick={() => {
                          setDeleteError("");
                          setDeleting(row);
                        }}
                      >
                        {t("common.delete")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {meta && meta.last_page > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t("common.previous")}
          </button>
          <span className="text-sm text-ink-2">
            {t("common.pageOf", { current: meta.current_page, last: meta.last_page })}
          </span>
          <button
            type="button"
            className="btn-secondary"
            disabled={page >= meta.last_page}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("common.next")}
          </button>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        title={t("providers.leave.deleteTitle")}
        message={t("providers.leave.deleteMessage")}
        busy={deleteBusy}
        error={deleteError}
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
}
